import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, Dumbbell, ShieldAlert, Check, ArrowRight, ArrowLeft, Sparkles,
} from "lucide-react";

const STORAGE_KEY = "fff_onboarding";
const FLAG_KEY = "fff_onboarded";

const GOALS = [
  { id: "lose_fat",     label: "Lose fat",        sub: "Cut while keeping muscle" },
  { id: "build_muscle", label: "Build muscle",    sub: "Hypertrophy focus" },
  { id: "get_stronger", label: "Get stronger",    sub: "Heavier compound lifts" },
  { id: "mobility",     label: "Move better",     sub: "Flexibility + mobility" },
  { id: "endurance",    label: "Endurance",       sub: "Cardio + conditioning" },
  { id: "maintain",     label: "Maintain & feel great", sub: "Balanced training" },
];

const EQUIPMENT = [
  "Bodyweight", "Dumbbells", "Barbell", "Kettlebell", "Resistance bands",
  "Pull-up bar", "Bench", "Cardio machine", "Yoga mat", "Medicine ball", "Stability ball",
];

const INJURIES = [
  "None", "Lower back", "Knee", "Shoulder", "Scapular/Upper back", "Neck", "Hip", "Elbow", "Wrist", "Ankle", "Hamstring",
];

const stepMeta = [
  { icon: Target,       title: "What's your main goal?", sub: "We'll shape every workout around this." },
  { icon: Dumbbell,     title: "What can you train with?", sub: "Pick everything you have access to." },
  { icon: ShieldAlert,  title: "Anything to work around?", sub: "We'll filter contraindicated moves." },
];

export const isOnboarded = () => localStorage.getItem(FLAG_KEY) === "1";
export const getOnboardingData = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
};

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState("");
  const [equipment, setEquipment] = useState(["Bodyweight"]);
  const [injuries, setInjuries] = useState(["None"]);

  const progress = useMemo(() => ((step + 1) / 3) * 100, [step]);
  const Icon = stepMeta[step].icon;

  const canNext = useMemo(() => {
    if (step === 0) return !!goal;
    if (step === 1) return equipment.length > 0;
    return injuries.length > 0;
  }, [step, goal, equipment, injuries]);

  const toggle = (arr, setArr, val, exclusive = null) => {
    if (exclusive && val === exclusive) { setArr([exclusive]); return; }
    let next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr.filter(x => x !== exclusive), val];
    if (next.length === 0) next = exclusive ? [exclusive] : next;
    setArr(next);
  };

  const handleNext = () => {
    if (step < 2) { setStep(step + 1); return; }
    const payload = {
      goal,
      equipment,
      injuries: injuries.filter((i) => i !== "None"),
      completed_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.setItem(FLAG_KEY, "1");
    onComplete && onComplete(payload);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0b0b0c] text-white overflow-y-auto"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="onboarding-screen"
    >
      <div className="mx-auto max-w-md px-6 pt-6 pb-32">
        {/* Progress bar */}
        <div className="flex items-center gap-2 mb-8">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex-1 h-1 rounded-full bg-zinc-800 overflow-hidden">
              <motion.div
                className="h-full bg-lime-400"
                initial={{ width: 0 }}
                animate={{ width: i <= step ? "100%" : "0%" }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-center mb-4">
          <motion.div
            key={step}
            initial={{ scale: 0.6, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 14 }}
            className="h-14 w-14 rounded-2xl bg-gradient-to-br from-lime-400 to-emerald-600 grid place-items-center shadow-lg"
          >
            <Icon className="h-7 w-7 text-black" />
          </motion.div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
          >
            <h1 className="text-2xl font-semibold tracking-tight text-center leading-tight">
              {stepMeta[step].title}
            </h1>
            <p className="text-center text-zinc-400 mt-1.5 text-sm">{stepMeta[step].sub}</p>

            {/* Step 1: goal (single select) */}
            {step === 0 && (
              <div className="mt-6 space-y-2.5" data-testid="onboarding-goal-list">
                {GOALS.map((g, idx) => {
                  const active = goal === g.id;
                  return (
                    <motion.button
                      key={g.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      onClick={() => setGoal(g.id)}
                      data-testid={`onboarding-goal-${g.id}`}
                      className={`w-full text-left rounded-2xl border p-4 transition-all ${
                        active ? "border-lime-400 bg-lime-400/10 ring-1 ring-lime-400" : "border-zinc-800 bg-zinc-900/60 hover:border-zinc-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{g.label}</div>
                          <div className="text-xs text-zinc-400 mt-0.5">{g.sub}</div>
                        </div>
                        <div className={`h-5 w-5 rounded-full grid place-items-center border-2 ${active ? "border-lime-400 bg-lime-400" : "border-zinc-600"}`}>
                          {active && <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Step 2: equipment (multi) */}
            {step === 1 && (
              <div className="mt-6 grid grid-cols-2 gap-2.5" data-testid="onboarding-equipment-list">
                {EQUIPMENT.map((e, idx) => {
                  const active = equipment.includes(e);
                  return (
                    <motion.button
                      key={e}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => toggle(equipment, setEquipment, e)}
                      data-testid={`onboarding-equipment-${e.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`rounded-xl border px-3 py-3 text-sm transition-all ${
                        active ? "border-lime-400 bg-lime-400/10 ring-1 ring-lime-400 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="leading-tight">{e}</span>
                        {active && <Check className="h-4 w-4 text-lime-400 shrink-0" strokeWidth={3} />}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Step 3: injuries (multi, with "None" exclusive) */}
            {step === 2 && (
              <>
                <div className="mt-6 grid grid-cols-2 gap-2.5" data-testid="onboarding-injuries-list">
                  {INJURIES.map((inj, idx) => {
                    const active = injuries.includes(inj);
                    return (
                      <motion.button
                        key={inj}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => toggle(injuries, setInjuries, inj, "None")}
                        data-testid={`onboarding-injury-${inj.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                        className={`rounded-xl border px-3 py-3 text-sm transition-all ${
                          active ? "border-lime-400 bg-lime-400/10 ring-1 ring-lime-400 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="leading-tight">{inj}</span>
                          {active && <Check className="h-4 w-4 text-lime-400 shrink-0" strokeWidth={3} />}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-zinc-500 leading-snug">
                  Have a specific pathology (rotator cuff, biceps tendinitis, meniscus…)? Pick the broad region here and refine in Settings → Plan later.
                </p>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-8 flex items-center gap-3">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              data-testid="onboarding-back-btn"
              className="rounded-full h-12 px-4 border border-zinc-800 bg-zinc-900/60 text-zinc-300 flex items-center gap-1.5 text-sm hover:border-zinc-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canNext}
            data-testid="onboarding-next-btn"
            className="flex-1 rounded-full h-12 bg-lime-400 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            {step < 2 ? <>Continue <ArrowRight className="h-4 w-4" /></> : <>See your plan <Sparkles className="h-4 w-4" /></>}
          </button>
        </div>

        <div className="text-center mt-4">
          <button
            onClick={() => { localStorage.setItem(FLAG_KEY, "1"); onComplete && onComplete({}); }}
            data-testid="onboarding-skip-btn"
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
